import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import enGB from 'date-fns/locale/en-GB';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import BookingPricePreview from '../Bookings/AddBooking/BookingPricePreview';
import RequestFundingSummary, { fundingGate } from './RequestFundingSummary';
import '../Bookings/addBooking.css';
import './pendingClientRequests.css';

registerLocale('en-GB', enGB);

const LAUNDRY_TYPES = [1, 5, 6];
const AMENITIES_TYPES = [1, 5];
const NO_CLEANER_TYPES = [6, 7, 8, 12];

const durationOptions = [
    { value: '1', label: '1 Hour' }, { value: '1.5', label: '1.5 Hours' },
    { value: '2', label: '2 Hours' }, { value: '2.5', label: '2.5 Hours' },
    { value: '3', label: '3 Hours' }, { value: '3.5', label: '3.5 Hours' },
    { value: '4', label: '4 Hours' }, { value: '4.5', label: '4.5 Hours' },
    { value: '5', label: '5 Hours' }, { value: '5.5', label: '5.5 Hours' },
    { value: '6', label: '6 Hours' },
];

const calculateDuration = (bedrooms, cleaners) => {
    switch (bedrooms) {
        case 0: return 2.5;
        case 1: return 3;
        case 2: return cleaners === 2 ? 2.5 : 4;
        case 3: return 3;
        case 4: return 4;
        default: return 2;
    }
};
const calculateEndTime = (start, duration) => start ? new Date(new Date(start).getTime() + duration * 3600000) : null;
const formatDateToString = (date) => {
    if (!(date instanceof Date)) date = new Date(date);
    const y = date.getFullYear(), mo = String(date.getMonth() + 1).padStart(2, '0'),
        d = String(date.getDate()).padStart(2, '0'), h = String(date.getHours()).padStart(2, '0'),
        mi = String(date.getMinutes()).padStart(2, '0'), s = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${mo}-${d} ${h}:${mi}:${s}`;
};

// Full editable "Add Booking"-style review of a client booking request.
// Client + property are locked (the request defines them); everything else
// (type, schedule, cleaners, add-ons, notes) is editable. Approve creates the
// booking from the admin's final values via the same trigger-based pricing.
export default function BookingRequestReview({ request, onClose, onDone, readOnlyOutcome = null }) {
    const p = request.payload || {};

    const [cleaners, setCleaners] = useState([]);
    const [properties, setProperties] = useState([]);
    const [bookingTypes, setBookingTypes] = useState([]);
    const [bookingStatus, setBookingStatus] = useState([]);
    const [addons, setAddons] = useState([]);
    const [unavailableCleaners, setUnavailableCleaners] = useState([]);

    const [formData, setFormData] = useState({
        client_id: request.client_id,
        property_id: p.property_id,
        type_id: p.type_id,
        status_id: '',
        booking_start: p.booking_start ? new Date(p.booking_start.replace(' ', 'T')) : null,
        booking_end: p.booking_end ? new Date(p.booking_end.replace(' ', 'T')) : null,
        required_cleaners: p.required_cleaners ?? 1,
        duration: p.duration_hours ?? 2,
        cleaner_ids: [],
        is_laundry: !!p.is_laundry,
        is_amenities: !!p.is_amenities,
        selected_addons: [],
        booking_specific_notes: p.notes || '',
        booking_warehouse_notes: '',
        custom_price_excl_vat: '',
        pending_reason: '',
    });

    const [editable, setEditable] = useState(false); // read-only until "Make Changes"
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [pendingMode, setPendingMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const rejectReasonRef = useRef(null);
    const pendingReasonRef = useRef(null);

    // The "Pending" booking status (if the org has one) drives the approve-as-pending
    // flow. Declared above the effects below because they depend on it.
    const pendingStatus = useMemo(() => bookingStatus.find((s) => (s.status_name || '').trim().toLowerCase() === 'pending'), [bookingStatus]);
    const selectedStatusIsPending = !!pendingStatus && formData.status_id === pendingStatus.id;

    // FUNDING DETAIL (2026-07-18). The create-booking review previously
    // fetched NOTHING about money — it showed a rail banner and a price and
    // nothing about what was already paid or what approval would debit. That is
    // the structural gap behind "it shows like that in some scenarios which is
    // completely broken": the surface that decides whether to move money showed
    // the least information about it. Best-effort and purely additive — a failed
    // or empty fetch degrades to the conservative fallback in
    // RequestFundingSummary, never to a guessed figure.
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        if (!request?.id) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/client-change-requests/${request.id}`);
                // GET /:id responds { request: {...} } — the funding summary and
                // funding_error hang off THAT inner row, not off res.data. Storing
                // res.data here meant detail.funding was always undefined and this
                // modal silently fell back to the "no server summary" path, which is
                // the exact A2 breakdown the funding block exists to prevent.
                if (!cancelled) setDetail(res.data?.request || null);
            } catch {
                /* Funding detail is context — never block the review modal. */
            }
        })();
        return () => { cancelled = true; };
    }, [request?.id]);

    // Bring the reason box (bottom of the form) into view + focus it when
    // entering reject mode, so it's obvious what's needed.
    useEffect(() => {
        if (rejectMode && rejectReasonRef.current) {
            rejectReasonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current.focus();
        }
    }, [rejectMode]);

    // Same for the "approve as pending" reason box — whether the admin entered
    // it via the button (pendingMode) or by picking "Pending" in the status
    // dropdown (selectedStatusIsPending).
    useEffect(() => {
        if ((pendingMode || selectedStatusIsPending) && pendingReasonRef.current) {
            pendingReasonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            pendingReasonRef.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingMode, selectedStatusIsPending]);

    const set = (updates) => setFormData((prev) => ({ ...prev, ...updates }));

    const maintenanceTypeId = useMemo(() => bookingTypes.find((t) => t.type === 'Maintenance')?.id, [bookingTypes]);
    const isMaintenanceType = formData.type_id === maintenanceTypeId;
    const isNoCleanerType = NO_CLEANER_TYPES.includes(formData.type_id);

    useEffect(() => {
        (async () => {
            try {
                const [cR, tR, sR, aR, prR] = await Promise.all([
                    axios.get('/api/team/role/?role=cleaner'),
                    axios.get('/api/bookings/booking_types'),
                    axios.get('/api/bookings/booking_status'),
                    axios.get('/api/bookings/addons'),
                    axios.get(`/api/properties/active?clientId=${request.client_id}`),
                ]);
                setCleaners(cR.data); setBookingTypes(tR.data); setBookingStatus(sR.data);
                setAddons(aR.data); setProperties(prR.data);
            } catch (e) { console.error('Error loading review data:', e); }
        })();
    }, [request.client_id]);

    // Preload the client's OWN picked add-ons into the editable list once the
    // add-on catalogue loads, so the reviewer sees them and they aren't dropped
    // on a no-edit approval. The auto linen deposit is NOT here (it stays auto
    // and renders as the read-only line above).
    useEffect(() => {
        if (!addons.length) return;
        const picks = Array.isArray(p.selected_addons) ? p.selected_addons : [];
        if (picks.length === 0) return;
        setFormData((prev) => {
            if (prev.selected_addons.length > 0) return prev; // already loaded / edited
            const mapped = picks
                .map((a) => {
                    const item = addons.find((x) => x.id === Number(a.item_id));
                    return item
                        ? { id: item.id, name: item.name, price: item.default_price, quantity: Number(a.quantity) || 1 }
                        : null;
                })
                .filter(Boolean);
            return mapped.length ? { ...prev, selected_addons: mapped } : prev;
        });
    }, [addons]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!formData.booking_start || !formData.booking_end) return;
        (async () => {
            try {
                const res = await axios.get('/api/bookings/cleaners/availability', {
                    params: { booking_start: formatDateToString(formData.booking_start), booking_end: formatDateToString(formData.booking_end) },
                });
                setUnavailableCleaners(res.data.map((c) => c.cleaner_id));
            } catch (e) { console.error(e); }
        })();
    }, [formData.booking_start, formData.booking_end]);

    const handleTypeChange = (opt) => {
        const typeId = opt ? opt.value : '';
        const isCleaner = !NO_CLEANER_TYPES.includes(typeId);
        const isMaintenance = typeId === maintenanceTypeId;
        set({
            type_id: typeId,
            required_cleaners: isMaintenance ? 0 : (isCleaner ? Math.max(1, formData.required_cleaners) : 0),
            cleaner_ids: isMaintenance ? [] : (isCleaner ? formData.cleaner_ids : []),
            duration: isMaintenance ? 1 : (isCleaner ? formData.duration : 0),
            is_laundry: isMaintenance ? false : LAUNDRY_TYPES.includes(typeId),
            is_amenities: isMaintenance ? false : AMENITIES_TYPES.includes(typeId),
            custom_price_excl_vat: '',
        });
    };

    const handleDateChange = (date) => {
        const end = isNoCleanerType ? date : calculateEndTime(date, formData.duration);
        set({ booking_start: new Date(date), booking_end: end ? new Date(end) : null });
    };
    const handleDurationChange = (opt) => {
        const dur = parseFloat(opt.value);
        set({ duration: dur, booking_end: calculateEndTime(formData.booking_start, dur) });
    };
    const handleCleanersCountChange = (e) => {
        const count = parseInt(e.target.value, 10) || 1;
        const prop = properties.find((pr) => pr.id === formData.property_id);
        const dur = prop ? calculateDuration(prop.bedrooms, count) : formData.duration;
        set({ required_cleaners: count, duration: dur, booking_end: calculateEndTime(formData.booking_start, dur) });
    };
    const handleAddonSelect = (selected) => {
        set({ selected_addons: selected ? selected.map((o) => ({ id: o.value, name: o.label, price: o.price, quantity: formData.selected_addons.find((a) => a.id === o.value)?.quantity || 1 })) : [] });
    };

    const buildAdminFields = () => ({
        type_id: formData.type_id,
        status_id: formData.status_id || null,
        booking_start: formatDateToString(formData.booking_start),
        booking_end: formData.booking_end ? formatDateToString(formData.booking_end) : formatDateToString(formData.booking_start),
        required_cleaners: isMaintenanceType ? 0 : formData.required_cleaners,
        cleaner_ids: isMaintenanceType ? [] : formData.cleaner_ids.filter(Boolean),
        is_laundry: formData.is_laundry,
        is_amenities: formData.is_amenities,
        selected_addons: formData.selected_addons.map((a) => ({ item_id: a.id, quantity: a.quantity })),
        custom_price_excl_vat: isMaintenanceType ? (parseFloat(formData.custom_price_excl_vat) || null) : null,
        booking_specific_notes: formData.booking_specific_notes,
        booking_warehouse_notes: formData.booking_warehouse_notes,
    });

    // Approve the request into a real booking. When `asPending` (or the admin
    // picked the "Pending" status in the dropdown), the booking is still added to
    // the schedule but flagged Pending and a reason is required — mirroring the
    // admin Add Booking form.
    const submitApproval = async ({ asPending }) => {
        const usePending = asPending || selectedStatusIsPending;
        if (usePending) {
            if (!pendingStatus) {
                setError('No "Pending" booking status is configured — cannot approve as pending.');
                return;
            }
            if (!formData.pending_reason.trim()) {
                setError('A pending reason is required.');
                setPendingMode(true);
                pendingReasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                pendingReasonRef.current?.focus();
                return;
            }
        }
        setSubmitting(true);
        setError('');
        try {
            const admin_fields = buildAdminFields();
            if (usePending) {
                admin_fields.status_id = pendingStatus.id;
                admin_fields.pending_reason = formData.pending_reason.trim();
            }
            const res = await axios.post(`/api/client-change-requests/${request.id}/approve`, { admin_fields });
            // Idempotent path: another admin already approved it, so none of the
            // edits made here were applied. Warn instead of a false "approved".
            const alreadyApproved = /already approved/i.test(res.data?.message || '');
            if (alreadyApproved) {
                window.alert('This request was already approved by someone else — your changes here were NOT applied. Refreshing the list.');
            }
            // Parent (handleDone) shows the toast + redirects to the new booking.
            onDone('approved', res.data?.message, res.data?.result_id);
        } catch (err) {
            console.error('Error approving booking request:', err);
            setError(err.response?.data?.error || 'Failed to approve request.');
        } finally {
            setSubmitting(false);
        }
    };

    // Guard dismissal while a submit is in flight — a failure after unmount
    // would swallow the error and any recovery instructions.
    const safeClose = () => { if (!submitting) onClose(); };

    const reject = async () => {
        if (!rejectReason.trim()) {
            setError('A rejection reason is required.');
            rejectReasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current?.focus();
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`/api/client-change-requests/${request.id}/reject`, { reason: rejectReason.trim() });
            onDone('rejected');
        } catch (err) {
            console.error('Error rejecting booking request:', err);
            setError(err.response?.data?.error || 'Failed to reject request.');
        } finally {
            setSubmitting(false);
        }
    };

    // F5. `funding_error` means the server could not prove where the money for
    // this booking would come from; `blockers` are the coded refusals the
    // create_booking approve branch hard-409s on. Either way the reviewer must
    // not be able to approve — until this landed the button stayed live and the
    // request failed (or worse, succeeded) after the fact.
    const gate = fundingGate(request, detail);

    return (
        <div className="pcr-modal-overlay" onClick={safeClose}>
            <div className="pcr-modal pcr-modal--form" onClick={(e) => e.stopPropagation()}>
                <div className="pcr-modal-header">
                    <div>
                        <h3>Review New Booking</h3>
                        <div className="pcr-modal-sub">
                            {request.client_display_name || request.client_company_name} · {p.property_label || `Property #${p.property_id}`} · requested by {request.requested_by_first_name} {request.requested_by_last_name}
                        </div>
                    </div>
                    <button className="pcr-modal-close" onClick={safeClose} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pcr-modal-body">
                    {!editable && (
                        <div className="pcr-readonly-note">
                            Read-only preview of the client&apos;s request. Click <strong>Make Changes</strong> to edit any field before approving.
                        </div>
                    )}
                    <div className={`new-booking-form pcr-review-form ${editable ? '' : 'pcr-review-form--locked'}`}>
                        {/* Card 1: Type, status, toggles */}
                        <div className="ab-card">
                            <div className="ab-card__head"><h3 className="ab-card__title">Booking Details</h3></div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Booking Type</label>
                                    <Select
                                        options={bookingTypes.map((t) => ({ value: t.id, label: t.type }))}
                                        value={bookingTypes.filter((t) => t.id === formData.type_id).map((t) => ({ value: t.id, label: t.type }))[0] || null}
                                        onChange={handleTypeChange} placeholder="Select type"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Booking Status</label>
                                    <Select
                                        options={bookingStatus.map((s) => ({ value: s.id, label: s.status_name }))}
                                        value={bookingStatus.filter((s) => s.id === formData.status_id).map((s) => ({ value: s.id, label: s.status_name }))[0] || null}
                                        onChange={(opt) => set({ status_id: opt ? opt.value : '' })}
                                        placeholder="Confirmed (default)" isClearable
                                    />
                                </div>
                                {isMaintenanceType && (
                                    <div className="form-group">
                                        <label>Price (excl. VAT, AED)</label>
                                        <input type="number" step="0.01" min="0.01" value={formData.custom_price_excl_vat} onChange={(e) => set({ custom_price_excl_vat: e.target.value })} placeholder="e.g. 250.00" />
                                    </div>
                                )}
                                {!isMaintenanceType && (
                                    <div className="form-group laundry">
                                        <label>Laundry</label>
                                        <label className="switch">
                                            <input type="checkbox" checked={formData.is_laundry} onChange={() => set({ is_laundry: !formData.is_laundry })} />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                                )}
                                {!isMaintenanceType && (
                                    <div className="form-group amenities">
                                        <label>Amenities</label>
                                        <label className="switch">
                                            <input type="checkbox" checked={formData.is_amenities} onChange={() => set({ is_amenities: !formData.is_amenities })} disabled={AMENITIES_TYPES.includes(formData.type_id)} />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Card 2: Client & Property (locked from the request) */}
                        <div className="ab-card">
                            <div className="ab-card__head"><h3 className="ab-card__title">Client &amp; Property</h3></div>
                            <div className="ab-grid">
                                <div className="form-group">
                                    <label>Client</label>
                                    <input type="text" value={request.client_display_name || request.client_company_name || ''} disabled />
                                </div>
                                <div className="form-group">
                                    <label>Property</label>
                                    <input type="text" value={p.property_label || `Property #${p.property_id}`} disabled />
                                </div>
                            </div>
                        </div>

                        {/* Card 3: Schedule & Cleaners */}
                        <div className="ab-card">
                            <div className="ab-card__head"><h3 className="ab-card__title">Schedule &amp; Cleaners</h3></div>
                            {!isNoCleanerType && (
                                <div className="ab-grid--3">
                                    {!isMaintenanceType && (
                                        <div className="form-group">
                                            <label>Required Cleaners</label>
                                            <input type="number" value={formData.required_cleaners} onChange={handleCleanersCountChange} min="1" />
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <label>Start Time</label>
                                        <DatePicker selected={formData.booking_start} onChange={handleDateChange} showTimeSelect timeFormat="HH:mm" timeIntervals={30} timeCaption="Time" locale="en-GB" dateFormat="dd/MM/yyyy HH:mm" placeholderText="Select Start Time" />
                                    </div>
                                    <div className="form-group">
                                        <label>Duration</label>
                                        <Select options={durationOptions} value={durationOptions.find((o) => o.value === String(formData.duration)) || null} onChange={handleDurationChange} placeholder="Select duration" />
                                    </div>
                                </div>
                            )}

                            {isNoCleanerType && (
                                <div style={{ maxWidth: 320 }}>
                                    <div className="form-group">
                                        <label>Date &amp; Time</label>
                                        <DatePicker selected={formData.booking_start} onChange={handleDateChange} showTimeSelect timeFormat="HH:mm" timeIntervals={30} timeCaption="Time" locale="en-GB" dateFormat="dd/MM/yyyy HH:mm" placeholderText="Select Date & Time" />
                                    </div>
                                </div>
                            )}

                            {!isNoCleanerType && !isMaintenanceType && formData.required_cleaners > 0 && (
                                <div className="ab-grid--4" style={{ marginTop: 14 }}>
                                    {Array.from({ length: formData.required_cleaners }).map((_, i) => (
                                        <div key={i} className="form-group">
                                            <label>Cleaner {i + 1}</label>
                                            <Select
                                                options={cleaners.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name}${unavailableCleaners.includes(c.id) ? ' (Unavailable)' : ''}`, isDisabled: unavailableCleaners.includes(c.id) }))}
                                                onChange={(opt) => { const ids = [...formData.cleaner_ids]; ids[i] = opt ? opt.value : null; set({ cleaner_ids: ids }); }}
                                                value={formData.cleaner_ids[i] ? { value: formData.cleaner_ids[i], label: cleaners.find((c) => c.id === formData.cleaner_ids[i])?.first_name } : null}
                                                placeholder={`Cleaner ${i + 1}`} isClearable
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Card 4: Add-ons & Notes */}
                        <div className="ab-card">
                            <div className="ab-card__head"><h3 className="ab-card__title">Extra Linens &amp; Notes</h3></div>

                            <div className="form-group">
                                <label>Extra Linens</label>
                                <Select options={addons.map((a) => ({ value: a.id, label: a.name, price: a.default_price }))} isMulti onChange={handleAddonSelect} placeholder="Select add-ons"
                                    value={formData.selected_addons.map((a) => ({ value: a.id, label: a.name, price: a.price }))} />
                                {formData.selected_addons.length > 0 && (
                                    <div className="addons-list">
                                        {formData.selected_addons.map((addon) => (
                                            <div key={addon.id} className="addon-item">
                                                <div className="addon-details">
                                                    <span className="addon-name">{addon.name}</span>
                                                    <span className="addon-unit-price">AED {addon.price}</span>
                                                </div>
                                                <input type="number" step="0.5" min="1" value={addon.quantity} className="addon-quantity"
                                                    onChange={(e) => set({ selected_addons: formData.selected_addons.map((a) => a.id === addon.id ? { ...a, quantity: parseFloat(e.target.value) || 1 } : a) })} />
                                                <span className="addon-total-price">AED {(addon.price * addon.quantity).toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="notes" style={{ marginTop: 14 }}>
                                <div className="form-group">
                                    <label>Booking Notes</label>
                                    <textarea value={formData.booking_specific_notes} onChange={(e) => set({ booking_specific_notes: e.target.value })} placeholder="Specific notes for this booking" />
                                </div>
                                <div className="form-group">
                                    <label>Warehouse Notes</label>
                                    <textarea value={formData.booking_warehouse_notes} onChange={(e) => set({ booking_warehouse_notes: e.target.value })} placeholder="Warehouse notes for this booking" />
                                </div>
                            </div>
                        </div>

                        {/* A5: the preview's own "Client Wallet" block never renders
                            here — how the client pays is stated exactly ONCE, by the
                            payment list below the price summary. Same setting the
                            admin Add/Edit Booking forms use. */}
                        {(formData.property_id || formData.type_id) && (
                            <BookingPricePreview
                                selectedAddons={formData.selected_addons} clientId={formData.client_id}
                                propertyId={formData.property_id} properties={properties}
                                typeId={formData.type_id} statusId={formData.status_id}
                                bookingStart={formData.booking_start} bookingEnd={formData.booking_end}
                                requiredCleaners={formData.required_cleaners} isAmenities={formData.is_amenities}
                                duration={formData.duration} isMaintenance={isMaintenanceType}
                                customPriceExclVat={formData.custom_price_excl_vat} bookingId={null}
                                showWallet={false}
                                // The auto linen deposit the client was quoted (VAT-free,
                                // per bed) MUST appear in the cost table so the total the
                                // admin approves matches what is actually charged.
                                autoLinenDepositBeds={p.estimate?.linen_deposit?.beds || 0}
                            />
                        )}
                    </div>

                    {/* A5 (2026-07-19): ONE clear payment list, at
                        the end, after the price summary — one row per funding leg
                        (wallet / card with date+card / auto payment / charged on
                        approval), balance-after when the wallet is debited, one
                        explanatory line. The banner, the wallet section and every
                        other restatement of how this was paid are gone: this list
                        is the only payment block on the screen. */}
                    <RequestFundingSummary readOnly={!!readOnlyOutcome}
                        request={request}
                        detail={detail}
                        cost={request.estimated_total}
                    />

                    {(pendingMode || selectedStatusIsPending) && !rejectMode && (
                        <div className="pcr-section pcr-reason-block pcr-reason-block--pending">
                            <div className="pcr-section-title">Pending reason (why this booking is on hold)</div>
                            <textarea ref={pendingReasonRef} className="pcr-reject-reason" value={formData.pending_reason} maxLength={500} placeholder="e.g. Awaiting client confirmation / deposit not yet received…" onChange={(e) => set({ pending_reason: e.target.value })} />
                        </div>
                    )}

                    {rejectMode && (
                        <div className="pcr-section pcr-reason-block pcr-reason-block--reject">
                            <div className="pcr-section-title">Rejection reason (visible to client)</div>
                            <div className="pcr-reason-presets">
                                <button
                                    type="button"
                                    className="pcr-reason-preset"
                                    onClick={() => setRejectReason('No availability at the requested time.')}
                                >
                                    No availability
                                </button>
                            </div>
                            <textarea ref={rejectReasonRef} className="pcr-reject-reason" value={rejectReason} maxLength={500} placeholder="Explain why this request is being rejected…" onChange={(e) => setRejectReason(e.target.value)} />
                        </div>
                    )}

                    {gate.blocked && !readOnlyOutcome && (
                        <div className="pcr-error">
                            <FontAwesomeIcon icon={faTriangleExclamation} /> {gate.reason}
                        </div>
                    )}

                    {!!error && <div className="pcr-error">{error}</div>}
                </div>

                {/* Read-only history view (2026-08-06): rejected/
                    cancelled/approved rows open for inspection — outcome
                    instead of action buttons. */}
                {readOnlyOutcome ? (
                    <div className="pcr-modal-actions" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{readOnlyOutcome.statusLabel}</strong>
                            {readOnlyOutcome.reviewerName ? <> by <strong>{readOnlyOutcome.reviewerName}</strong></> : null}
                            {readOnlyOutcome.reviewedAt ? <> · {readOnlyOutcome.reviewedAt}</> : null}
                            {readOnlyOutcome.reason ? <div><strong>Reason:</strong> {readOnlyOutcome.reason}</div> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {readOnlyOutcome.onOpenEntity ? (
                                <button className="pcr-btn pcr-btn--primary" onClick={readOnlyOutcome.onOpenEntity}>
                                    {readOnlyOutcome.entityLabel}
                                </button>
                            ) : null}
                            <button className="pcr-btn pcr-btn--ghost" onClick={onClose}>Close</button>
                        </div>
                    </div>
                ) : (
                <div className="pcr-modal-actions pcr-modal-actions--split">
                    <div className="pcr-actions-left">
                        {!rejectMode && !pendingMode && !editable && (
                            <button className="pcr-btn pcr-btn--ghost" onClick={() => setEditable(true)} disabled={submitting}>
                                Make Changes
                            </button>
                        )}
                    </div>
                    <div className="pcr-actions-right">
                        {rejectMode ? (
                            <>
                                <button className="pcr-btn pcr-btn--ghost" onClick={() => { setRejectMode(false); setError(''); }} disabled={submitting}>Back</button>
                                <button className="pcr-btn pcr-btn--danger" onClick={reject} disabled={submitting}>{submitting ? 'Rejecting…' : 'Confirm Reject'}</button>
                            </>
                        ) : pendingMode ? (
                            <>
                                <button className="pcr-btn pcr-btn--ghost" onClick={() => { setPendingMode(false); setError(''); }} disabled={submitting}>Back</button>
                                <button className="pcr-btn pcr-btn--warning" onClick={() => submitApproval({ asPending: true })} disabled={submitting || gate.blocked} title={gate.blocked ? gate.reason : undefined}>{submitting ? 'Saving…' : 'Confirm — Create as Pending'}</button>
                            </>
                        ) : (
                            <>
                                <button className="pcr-btn pcr-btn--ghost pcr-btn--danger-text" onClick={() => { setRejectMode(true); setError(''); }} disabled={submitting}>Reject</button>
                                {pendingStatus && !selectedStatusIsPending && (
                                    <button className="pcr-btn pcr-btn--warning-outline" onClick={() => { setPendingMode(true); setError(''); }} disabled={submitting || gate.blocked} title={gate.blocked ? gate.reason : undefined}>Approve as Pending</button>
                                )}
                                <button className="pcr-btn pcr-btn--primary" onClick={() => submitApproval({ asPending: false })} disabled={submitting || gate.blocked} title={gate.blocked ? gate.reason : undefined}>
                                    {submitting ? 'Approving…' : (selectedStatusIsPending ? 'Approve as Pending' : 'Approve & Create Booking')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
                )}
            </div>
        </div>
    );
}
